// src/pages/LoginImproved.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "../components/ui";
import { useToast } from "../hooks/useToast";
import { useFormValidation } from "../hooks/useFormValidation";

const LoginImproved: React.FC = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showError, showSuccess, ToastContainer } = useToast();

  const { errors, validateForm, handleBlur } = useFormValidation({
    username: {
      required: true,
      minLength: 3,
      maxLength: 50
    },
    password: {
      required: true,
      minLength: 6
    }
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      // Save token and user info
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      showSuccess(`Welcome back, ${data.user.username}!`);
      
      // Small delay to show success message
      setTimeout(() => {
        navigate("/");
      }, 1000);

    } catch (error: any) {
      showError(error.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="w-full max-w-md">
          {/* Logo/Header Section */}
          <div className="text-center mb-8">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-2xl font-bold">🚗</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Vehicle Billing</h1>
            <p className="text-gray-600 mt-2">Sign in to your account</p>
          </div>

          {/* Login Form */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                type="text"
                label="Username"
                placeholder="Enter your username"
                value={formData.username}
                onChange={(e) => handleInputChange("username", e.target.value)}
                onBlur={(e) => handleBlur("username", e.target.value)}
                error={errors.username}
                required
                autoComplete="username"
              />

              <Input
                type="password"
                label="Password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                onBlur={(e) => handleBlur("password", e.target.value)}
                error={errors.password}
                required
                autoComplete="current-password"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={!formData.username || !formData.password}
              >
                {loading ? "Signing In..." : "Sign In"}
              </Button>
            </form>

            {/* Additional Links */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Need help?{" "}
                <button 
                  type="button"
                  className="text-blue-600 hover:text-blue-800 font-medium"
                  onClick={() => showError("Please contact your administrator for support.")}
                >
                  Contact Support
                </button>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">
              © 2025 Vehicle Billing System. All rights reserved.
            </p>
          </div>
        </div>
      </div>
      
      <ToastContainer />
    </>
  );
};

export default LoginImproved;